'use client';

import { useState, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import CharacterSelectSheet from './CharacterSelectSheet';
import { Box, Typography, ButtonBase } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/components/icons';
import IconPressable from '@/components/IconPressable';
import {
  TEMP_FG,
  TEMP_FG_PRIMARY,
  TEMP_FG_INVERSE,
  TEMP_FG_LOWER,
  TEMP_FG_DISABLED_HIGH,
  TEMP_BG_TRANSPARENT,
} from '@/constants/tempColors';
import {
  TEMP_TITLE_EXTRABOLD_XL,
  TEMP_TITLE_BOLD_M,
  TEMP_TEXT_SEMIBOLD_XL,
  TEMP_TEXT_MEDIUM_M,
  TEMP_TEXT_MEDIUM_L,
} from '@/constants/tempTypo';
import {
  getCharacterRelation,
  getCharacterBasicInfo,
  useItem,
  getInventory,
} from '@/api/feedApi';
import type { GachaPullResultItem } from '@/api/feedApi';
import type { UserItemListItemDto, ITEM_GRADE } from '@/shared/types/api/Api';
import { getAssetUrl } from '@/utils/assetUrl';
import { getLocalizedText } from '@/utils/getLocalizedText';
import { useToastStore } from '@/stores/toastStore';
import { useTranslator } from '@/lang';
import { getErrorMessage } from '@/utils/errorMessages';
import { useStackNavigation } from '@/hooks/useStackNavigation';
import { APP_MAX_WIDTH } from '@/components/PageContent/PageContent';
import Portal from '@/components/Portal/Portal';
import { useLockedAssetSrc } from '@/hooks/useForcedBlur';

// 등급별 타이틀 색상
const GRADE_TITLE_COLORS: Record<string, string> = {
  N: '#FFFFFF',
  R: '#FFFFFF',
  S: '#FFFFFF',
  U: '#c084fc',
  L: '#ffe66a',
};

// 등급별 라벨
const GRADE_LABELS: Record<string, string> = {
  N: 'NORMAL',
  R: 'RARE',
  S: 'SPECIAL',
  U: 'ULTRA',
  L: 'LEGENDARY',
};

const GRADE_BORDER: Record<string, { gradient: string; shadow?: string }> = {
  L: { gradient: 'linear-gradient(180deg, #FFE76F 0%, #8000FF 100%)', shadow: '0 0 10px rgba(128,0,255,0.5)' },
  U: { gradient: 'linear-gradient(180deg, #DDB139 0%, #462300 100%)' },
  S: { gradient: 'linear-gradient(180deg, #DADADA 0%, #5A5A5A 100%)' },
  R: { gradient: 'linear-gradient(180deg, #4777FF 0%, #0F46E4 100%)' },
  N: { gradient: 'linear-gradient(180deg, #834D10 0%, #5F390F 100%)' },
};

function getEffectSrc(grade: string): string {
  return (grade === 'L' || grade === 'U')
    ? '/assets/items/L-effect.svg'
    : '/assets/items/common-effect.svg';
}

function Divider() {
  return <Box sx={{ height: '0.5px', bgcolor: 'rgba(255,255,255,0.15)' }} />;
}

/** 내부 정규화된 아이템 데이터 */
interface NormalizedItem {
  name: string;
  grade: string;
  characterId: string | null;
  minIntimacy: number;
  requireGirlfriend: boolean;
  imagePath: string | null;
  rewardType: string | null;
  rewardAssetType: string | null;
  intimacyReward: number;
  hasVoice: boolean;
  isPiece: boolean;
  pieceAmount: number | null;
  quantity: number;
  userItemId: string | null;
  itemKey: string | null;
}

function normalizeFromGacha(item: GachaPullResultItem, language: string): NormalizedItem {
  return {
    name: item.name?.[language] || item.name?.['ko'] || '',
    grade: item.grade || 'N',
    characterId: item.character?.id || null,
    minIntimacy: item.minIntimacy,
    requireGirlfriend: item.requireGirlfriend,
    imagePath: item.displayImagePath,
    rewardType: item.rewardType || null,
    rewardAssetType: item.rewardAssetType || null,
    intimacyReward: item.intimacyReward,
    hasVoice: !!item.voicePath,
    isPiece: item.itemType === 'PIECE' || !!item.pieceAmount,
    pieceAmount: item.pieceAmount,
    quantity: item.quantity,
    userItemId: null,
    itemKey: item.key || null,
  };
}

function normalizeFromUserItem(userItem: UserItemListItemDto, language: string): NormalizedItem {
  const item = userItem.Item;
  return {
    name: getLocalizedText(item.name, language),
    grade: item.grade || 'N',
    characterId: item.characterId || null,
    minIntimacy: item.minIntimacy,
    requireGirlfriend: item.requireGirlfriend,
    imagePath: item.imagePath || null,
    rewardType: item.rewardType || null,
    rewardAssetType: item.RewardAsset?.assetType || null,
    intimacyReward: item.intimacyReward,
    hasVoice: !!item.RewardAsset,
    isPiece: item.itemType === 'PIECE' || item.rewardType === 'PIECE_REWARD' || (item.key || '').startsWith('piece_'),
    itemKey: item.key || null,
    pieceAmount: item.itemType === 'PIECE' ? userItem.quantity : null,
    quantity: userItem.quantity,
    userItemId: userItem.id,
  };
}

interface GachaItemDetailProps {
  /** 뽑기 결과 아이템 */
  gachaItem?: GachaPullResultItem;
  /** 소유 아이템 */
  userItem?: UserItemListItemDto;
  language: string;
  onClose: () => void;
  /** 사용하기/교환하기 버튼 클릭 시 (뽑기 결과에서 내 아이템으로 이동 등) */
  onAction?: () => void;
  /** true이면 position:fixed 모달, false이면 일반 full-screen (기본: userItem이면 true) */
  asModal?: boolean;
  /** 채팅방에서 사용 시 캐릭터 선택 건너뛰고 바로 사용 */
  targetCharacterId?: string;
}

export default function GachaItemDetail({ gachaItem, userItem, language, onClose, onAction, asModal, targetCharacterId }: GachaItemDetailProps) {
  const isModal = asModal ?? !!userItem;
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const { t, f } = useTranslator();
  const { push } = useStackNavigation();
  const [isUsing, setIsUsing] = useState(false);
  const [showCharacterSheet, setShowCharacterSheet] = useState(false);

  // 데이터 정규화
  const data: NormalizedItem = userItem
    ? normalizeFromUserItem(userItem, language)
    : gachaItem
      ? normalizeFromGacha(gachaItem, language)
      : normalizeFromGacha({} as GachaPullResultItem, language);

  const grade = data.grade;
  // 에셋 보상 아이템 이미지는 곧 미해금 에셋 원본 — 성인 미인증이면 블러 대신 플레이스홀더
  const lockedSrc = useLockedAssetSrc();
  const rawImgSrc = data.imagePath ? getAssetUrl(data.imagePath, 'thumb') : null;
  const imgSrc = data.rewardType === 'ASSET_REWARD' ? lockedSrc(rawImgSrc) : rawImgSrc || '/images/blur.png';
  const gradeLabel = GRADE_LABELS[grade] || 'NORMAL';
  const gradeTitleColor = GRADE_TITLE_COLORS[grade] || '#FFFFFF';

  // 캐릭터 기본 정보 (이름 표시용)
  const { data: characterInfo } = useQuery({
    queryKey: ['character-basic', data.characterId],
    queryFn: () => getCharacterBasicInfo(data.characterId!),
    enabled: !!data.characterId,
  });

  const characterName = characterInfo?.name
    ? getLocalizedText(characterInfo.name, language)
    : '';

  // 캐릭터 관계 조회 (조건 달성 여부)
  const { data: relation } = useQuery({
    queryKey: ['character-relation', data.characterId],
    queryFn: () => getCharacterRelation(data.characterId!),
    enabled: !!data.characterId,
  });

  const currentIntimacy = relation?.state?.intimacy ?? 0;
  const isGirlfriend = relation?.state?.isGirlfriend ?? false;

  // 조건 달성 여부
  const intimacyMet = currentIntimacy >= data.minIntimacy;
  const girlfriendMet = !data.requireGirlfriend || isGirlfriend;
  const canUse = intimacyMet && girlfriendMet;

  // 섹션 표시 여부
  const hasCondition = !!data.characterId || data.minIntimacy > 0 || data.requireGirlfriend;
  const hasImageReward = data.rewardAssetType === 'IMAGE';
  const hasVideoReward = data.rewardAssetType === 'VIDEO';
  const hasReward = hasImageReward || hasVideoReward || data.hasVoice || data.intimacyReward > 0;

  // 타이틀
  const titleText = data.isPiece ? 'PIECE ITEM' : `${gradeLabel} Item`;
  const subtitleText = data.isPiece ? f(t('gacha.piece_count'), [data.pieceAmount ?? data.quantity]) : data.name;

  // 버튼 텍스트
  const actionText = data.isPiece ? t('gacha.exchange') : t('gacha.use_item');

  // 사용하기 버튼 → 캐릭터 선택 시트 열기
  const [resolvedUserItemId, setResolvedUserItemId] = useState<string | null>(null);
  const activeUserItemId = data.userItemId || resolvedUserItemId;

  const handleAction = useCallback(async () => {
    if (data.isPiece) {
      onClose();
      push('/my-items/collection');
      return;
    }
    if (targetCharacterId && activeUserItemId) {
      handleUseItem(targetCharacterId);
      return;
    }
    if (activeUserItemId) {
      setShowCharacterSheet(true);
    } else if (data.itemKey) {
      try {
        const inv = await queryClient.fetchQuery({ queryKey: ['inventory', 'grade', 'desc'], queryFn: () => getInventory({ sort: 'grade', sortDirection: 'desc' }) });
        const match = inv.items.find((item) => item.Item.key === data.itemKey);
        if (match) {
          setResolvedUserItemId(match.id);
          setShowCharacterSheet(true);
        } else {
          onAction?.();
        }
      } catch {
        onAction?.();
      }
    } else {
      onAction?.();
    }
  }, [activeUserItemId, data.isPiece, data.itemKey, onAction, onClose, push, queryClient]);

  // 캐릭터 선택 후 아이템 사용
  const handleUseItem = useCallback(async (characterId: string) => {
    if (!activeUserItemId || isUsing) return;
    setIsUsing(true);
    try {
      const result = await useItem(activeUserItemId, {
        characterId,
        locale: language,
      });
      if (result.success) {
        showToast('', t('gacha.use_success'));
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        queryClient.invalidateQueries({ queryKey: ['chat-room-items'] });
        queryClient.invalidateQueries({ queryKey: ['character-relation', characterId] });
        queryClient.invalidateQueries({ queryKey: ['bg-feed', characterId] });
        setShowCharacterSheet(false);
        onClose();
      }
    } catch (err: any) {
      const code = err?.response?.data?.message;
      // 캐릭터 전용 아이템인데 다른 캐릭터에게 사용 시 → 캐릭터 이름 포함 메시지
      if (code === 'ITEM_CHARACTER_MISMATCH' && characterName) {
        showToast('', f(t('error.item_character_only'), [characterName]));
      } else {
        showToast('', getErrorMessage(code, t));
      }
    } finally {
      setIsUsing(false);
    }
  }, [activeUserItemId, isUsing, language, showToast, queryClient, onClose, t, f, characterName]);

  const isActionDisabled = isUsing;

  // 모달 변형은 반드시 body로 portal — 스택 화면(transform)·스크롤 컨테이너 안에 두면
  // iOS에서 position:fixed가 갇혀 페이지 하단 버튼이 모달 위에 그려짐
  const detail = (
    <Box
      sx={{
        ...(isModal
          ? {
              position: 'fixed',
              top: 0,
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100%',
              maxWidth: APP_MAX_WIDTH,
              zIndex: 1600,
            }
          : { position: 'relative', height: '100dvh', maxWidth: APP_MAX_WIDTH, mx: 'auto' }),
        bgcolor: TEMP_FG,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* 오버레이 */}
      <Box sx={{ position: 'absolute', inset: 0, bgcolor: TEMP_BG_TRANSPARENT, pointerEvents: 'none' }} />

      {/* Header */}
      <Box sx={{ pt: 'var(--safe-area-top, 0px)', flexShrink: 0, position: 'relative', zIndex: 1 }}>
        <Box sx={{ height: 64, display: 'flex', alignItems: 'center', minHeight: 60, py: '14px' }}>
          <Box sx={{ pl: '20px', pr: '4px', py: '6px' }}>
            <IconPressable name="icClose" size={24} color={TEMP_FG_INVERSE} onPress={onClose} />
          </Box>
        </Box>
      </Box>

      {/* Content — 중앙 정렬 */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
          minHeight: 0,
          overflow: 'visible',
        }}
      >
        {/* 등급 라벨 + 아이템 이름 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          <Typography
            sx={{
              ...TEMP_TITLE_EXTRABOLD_XL,
              color: gradeTitleColor,
              textTransform: 'uppercase',
            }}
          >
            {titleText}
          </Typography>
          <Typography sx={{ ...TEMP_TITLE_BOLD_M, color: TEMP_FG_INVERSE, textAlign: 'center' }}>
            {subtitleText}
          </Typography>
        </Box>

        {/* 이펙트 + 아이템 이미지 + 프레임 */}
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            width: '100%',
            maxWidth: 390,
            maxHeight: 390,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 0,
          }}
        >
          {/* 배경 이펙트 SVG */}
          <Box
            component="img"
            src={getEffectSrc(grade)}
            alt=""
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: grade === 'L' ? 0.8 : grade === 'U' ? 0.75 : 0.6,
              pointerEvents: 'none',
              animation: 'itemEffectSpin 12s linear infinite',
              '@keyframes itemEffectSpin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
              ...(grade === 'U' && { filter: 'hue-rotate(270deg) saturate(1.4)' }),
            }}
          />

          {/* 아이템 이미지 + CSS 보더 */}
          <Box sx={{
            position: 'relative',
            width: 200, height: 200,
            borderRadius: '12px', overflow: 'hidden',
            background: GRADE_BORDER[grade]?.gradient || '#555',
            padding: '6px',
            ...(grade === 'L' && { boxShadow: GRADE_BORDER.L.shadow }),
          }}>
            <Box sx={{ width: '100%', height: '100%', borderRadius: '8px', overflow: 'hidden', bgcolor: '#eae8e8', position: 'relative' }}>
              <Box
                component="img"
                src={imgSrc}
                alt={data.name}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = '/images/blur.png'; }}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', ...(data.rewardType === 'ASSET_REWARD' && imgSrc !== '/images/blur.png' && { filter: 'blur(8px)' }) }}
              />

              <Typography
                sx={{
                  position: 'absolute',
                  top: -2,
                  left: 11,
                  fontSize: 46,
                  fontWeight: 800,
                  lineHeight: 1.4,
                  color: gradeTitleColor,
                  textShadow: '0px 0px 17px rgba(255,255,255,0.45)',
                  ...(grade === 'L' && {
                    background: 'linear-gradient(180deg, #fdffcf 0%, #ffeecf 26.92%, #9909f9 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0px 0px 17px rgba(255,255,255,0.45))',
                  }),
                }}
              >
                {grade}
              </Typography>

              {data.isPiece && (
                <Box sx={{
                  position: 'absolute', bottom: -1, right: -1,
                  width: 56, height: 50, borderTopLeftRadius: '12px',
                  bgcolor: 'rgba(255,252,252,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, fontSize: 24, color: TEMP_FG_INVERSE }}>
                    {data.pieceAmount ?? data.quantity}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* 상하 글로우 라인 */}
            <Box sx={{ position: 'absolute', top: 3, left: '50%', transform: 'translateX(-50%)', width: 164, height: 2.3, background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, #fff 50%, rgba(255,255,255,0) 100%)', zIndex: 2, pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', bottom: 3, left: '50%', transform: 'translateX(-50%)', width: 164, height: 2.3, background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, #fff 50%, rgba(255,255,255,0) 100%)', zIndex: 2, pointerEvents: 'none' }} />
          </Box>
        </Box>

        {/* 조건 + 보상 섹션 */}
        {(hasCondition || hasReward) && (
          <Box sx={{ width: 335, px: '8px', flexShrink: 0 }}>
            {hasCondition && (
              <>
                <Divider />
                <Box sx={{ display: 'flex', gap: '12px', py: '12px', px: '52px', alignItems: 'flex-start' }}>
                  <Box sx={{ width: 32, display: 'flex', justifyContent: 'center', pt: '2px' }}>
                    <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: TEMP_FG_LOWER }}>{t('gacha.condition')}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', px: '4px' }}>
                    {data.characterId && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minHeight: 21 }}>
                        <Icon name="icMypage" size={16} color={TEMP_FG_INVERSE} />
                        <Typography sx={{ ...TEMP_TEXT_MEDIUM_L, color: TEMP_FG_INVERSE }}>
                          {characterName || '\u00A0'}
                        </Typography>
                      </Box>
                    )}
                    {data.minIntimacy > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: intimacyMet ? 1 : 0.3 }}>
                        <Icon name="icHeartFilled" size={16} color={TEMP_FG_INVERSE} />
                        <Typography sx={{ ...TEMP_TEXT_MEDIUM_L, color: TEMP_FG_INVERSE }}>
                          {f(t('gacha.intimacy_above_celsius'), [data.minIntimacy])}
                        </Typography>
                      </Box>
                    )}
                    {data.requireGirlfriend && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: girlfriendMet ? 1 : 0.3 }}>
                        <Icon name="icHeartFilled" size={16} color={TEMP_FG_INVERSE} />
                        <Typography sx={{ ...TEMP_TEXT_MEDIUM_L, color: TEMP_FG_INVERSE }}>{t('gacha.girlfriend_required')}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </>
            )}

            {hasReward && (
              <>
                <Divider />
                <Box sx={{ display: 'flex', gap: '12px', py: '12px', px: '52px' }}>
                  <Box sx={{ width: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: TEMP_FG_LOWER }}>{t('gacha.reward')}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px', px: '4px' }}>
                    {data.isPiece ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <Typography sx={{ ...TEMP_TEXT_MEDIUM_L, color: TEMP_FG_INVERSE, lineHeight: 1.5 }}>
                          {t('gacha.piece_duplicate_title')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: TEMP_FG_LOWER }}>{t('gacha.piece_duplicate_s')}</Typography>
                          <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: TEMP_FG_LOWER }}>{t('gacha.piece_duplicate_u')}</Typography>
                          <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: TEMP_FG_LOWER }}>{t('gacha.piece_duplicate_l')}</Typography>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        {hasImageReward && (
                          <>
                            <Icon name="icImage" size={16} color={TEMP_FG_INVERSE} />
                            <Typography sx={{ ...TEMP_TEXT_MEDIUM_L, color: TEMP_FG_INVERSE }}>{t('gacha.image_reward')}</Typography>
                          </>
                        )}
                        {hasVideoReward && (
                          <>
                            <Icon name="icPlayFill" size={16} color={TEMP_FG_INVERSE} />
                            <Typography sx={{ ...TEMP_TEXT_MEDIUM_L, color: TEMP_FG_INVERSE }}>{t('gacha.video_reward')}</Typography>
                          </>
                        )}
                        {data.hasVoice && (
                          <Box sx={{ bgcolor: TEMP_FG, borderRadius: '9999px', p: '6px', display: 'flex', alignItems: 'center' }}>
                            <Icon name="icVolume" size={12} color={TEMP_FG_INVERSE} />
                          </Box>
                        )}
                        {data.intimacyReward > 0 && !hasImageReward && !hasVideoReward && (
                          <>
                            <Icon name="icHeartFilled" size={16} color={TEMP_FG_INVERSE} />
                            <Typography sx={{ ...TEMP_TEXT_MEDIUM_L, color: TEMP_FG_INVERSE }}>+ {data.intimacyReward}</Typography>
                          </>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </>
            )}

            <Divider />
          </Box>
        )}
      </Box>

      {/* 하단 버튼 */}
      <Box
        sx={{
          flexShrink: 0,
          px: '20px',
          pb: isModal ? 'calc(var(--safe-area-bottom, 0px) + 12px)' : '12px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <ButtonBase
            onClick={onClose}
            sx={{
              flex: '1 0 0',
              bgcolor: TEMP_FG_INVERSE,
              borderRadius: '16px',
              py: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG }}>{t('gacha.exit')}</Typography>
          </ButtonBase>
          <ButtonBase
            onClick={handleAction}
            disabled={isActionDisabled}
            sx={{
              flex: '1 0 0',
              bgcolor: isActionDisabled ? TEMP_FG_DISABLED_HIGH : TEMP_FG_PRIMARY,
              borderRadius: '16px',
              py: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isUsing ? 0.7 : 1,
            }}
          >
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>
              {isUsing ? t('gacha.using') : actionText}
            </Typography>
          </ButtonBase>
        </Box>
      </Box>

      {/* 캐릭터 선택 바텀시트 */}
      <AnimatePresence>
        {showCharacterSheet && (
          <CharacterSelectSheet
            minIntimacy={data.minIntimacy}
            requireGirlfriend={data.requireGirlfriend}
            isIntimacyItem={data.intimacyReward > 0}
            language={language}
            onSelect={handleUseItem}
            onClose={() => setShowCharacterSheet(false)}
          />
        )}
      </AnimatePresence>
    </Box>
  );

  return isModal ? <Portal>{detail}</Portal> : detail;
}
